import { isClient } from '@vueuse/core'
import { AppUtils } from '@/utils/appUtils'
import { TokenKey, clearLocalAuthState, getToken, go2LoginPage } from '@/utils/auth'
import { getFirstDomain } from '@/utils'
import {
  is401WhitelistPath,
  is401WhitelistRoute,
} from '~/utils/auth401Whitelist'
import { Locale as VantLocale } from 'vant'
import enUS from 'vant/es/locale/lang/en-US'
import zhCN from 'vant/es/locale/lang/zh-CN'

// 全局变量，用于客户端生命周期内的状态锁定
let appUtils: any = null
let isInitialLoaded = false

if (isClient) {
  appUtils = new AppUtils()
}

const hasLanguagePrefix = (path: string) => {
  return /^\/(en|cn)\//.test(path)
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  console.log('running global auth middleware.', to.fullPath)
  const { $i18n } = useNuxtApp()
  const mySystemStore = useMySystemStore()
  const myUserStore = useMyUserStore()
  const { public: PublicConfig } = useRuntimeConfig()

  mySystemStore.setProxyApi(PublicConfig.PROD_CLIENT_PROXY_API)
  const { finish } = useLoadingIndicator()

  // 1. 语言与国际化配置
  if (to.params?.lang && (to.params.lang === 'cn' || to.params.lang === 'en')) {
    const lang = to.params.lang === 'en' ? 'en-US' : 'zh-CN'
    const vantLocale = to.params.lang === 'en' ? enUS : zhCN
    // setLocale 是异步的，必须 await，否则 i18n.locale 会一直停在 defaultLocale(en-US)
    await $i18n.setLocale(lang)
    VantLocale.use(lang, vantLocale)
    mySystemStore.setLanguage(lang)
    mySystemStore.setTargetPageCode((to.meta.pageCode as string) ?? '')
  } else if (!hasLanguagePrefix(to.path)) {
    const currentLang = mySystemStore.language === 'en-US' ? 'en' : 'cn'
    return navigateTo(`/${currentLang}${to.fullPath}`)
  }

  // 2. 页面 Header 样式配置
  const { changeHeaderIsWhite, changeNoHeader, changeAppFlag } = mySystemStore
  if (appUtils?.isApp) changeAppFlag(true)
  changeHeaderIsWhite(!!to.meta?.headerIsWhite)
  changeNoHeader(!!to.meta?.noHeader)

  // 3. 仅限客户端执行的逻辑
  if (isClient) {
    // 仅 path 变化时关菜单：登录后 replace 清 prjId 会再进中间件，避免把刚打开的菜单关掉
    if (from.path !== to.path) {
      mySystemStore.closeHeaderPopup()
    }
    await setProjectId(to)

    // 只有在非 exchange 流程且未初始化过时才获取 APP 信息
    if (!isInitialLoaded && !to.query?.exchange) {
      await setAppConfig()
      isInitialLoaded = true
    }

    setTimeout(() => {
      finish()
    }, 3000)

    // 匿名公开页：不阻塞等待 fingerprint / 用户信息（已登录路径不变）
    if (!to.query?.exchange && !getToken()) {
      setClientId().catch(() => undefined)
      if (to.meta.needToken) {
        go2LoginPage(to.path)
        return
      }
      myUserStore.resetUserCompInfo()
      return
    }

    await setClientId()

    // 核心改进：执行授权与静默刷新逻辑
    return await handleAuthAndSilentRefresh(to)
  }
})

/**
 * 核心逻辑：授权校验与静默数据更新
 * - 白名单公开页 + 有缓存：先放行，后台静默刷新
 * - needToken / 非白名单：必须 await 校验，失败则先跳登录，避免先闪目标页
 */
const handleAuthAndSilentRefresh = async (route: any) => {
  const { setAuthExchange, logout, initTokenFromCookie } = useMySystemStore()
  const myUserStore = useMyUserStore()
  const exchangeStr = route.query?.exchange

  const isPublicWhitelist =
    is401WhitelistRoute(route) || is401WhitelistPath(route.path)
  const mustAwaitAuth = !!route.meta.needToken || !isPublicWhitelist

  // 路径 A：处理 Exchange (登录回跳，必须 await 换取 Token)
  if (exchangeStr) {
    const domain = getFirstDomain() || undefined
    const tokenCookie = useCookie(TokenKey, { path: '/', domain })
    tokenCookie.value = null

    try {
      await setAuthExchange({ code: exchangeStr as string })
      const newQuery = { ...route.query }
      delete newQuery.exchange
      return navigateTo({ path: route.path, query: newQuery, replace: true })
    } catch (e) {
      console.error('Exchange failed', e)
      clearLocalAuthState()
      if (mustAwaitAuth) {
        go2LoginPage(route.fullPath || route.path)
        return
      }
    }
  }

  // 路径 B：正常登录状态
  const token = getToken()
  if (token) {
    initTokenFromCookie()

    const hasCachedComp = !!myUserStore.compInfo?.compId
    // 仅公开白名单且有缓存时才静默；其余（含黑名单详情）必须 await
    const canSilentRefresh = hasCachedComp && isPublicWhitelist && !route.meta.needToken

    if (!canSilentRefresh) {
      try {
        await myUserStore.getUserCompanyInfo()
        await myUserStore.getJoinCompanyAudit()
      } catch (error: any) {
        const authInvalid = !getToken() || error?.code === 401
        if (authInvalid) {
          clearLocalAuthState()
        }
        // 鉴权失效：needToken / 非白名单页进登录，避免先闪目标页
        if (authInvalid && (route.meta.needToken || mustAwaitAuth)) {
          try {
            await logout()
          } catch {
            clearLocalAuthState()
          }
          go2LoginPage(route.fullPath || route.path)
          return
        }
      }
    } else {
      // 情况 2: 白名单公开页已有缓存，先放行，后台静默刷新
      console.log(
        '🍉 ~ 01auth.global.ts ~ handleAuthAndSilentRefresh ~ whitelist cached, silent refresh.',
      )
      myUserStore
        .getUserCompanyInfo()
        .then(() => {
          myUserStore.getJoinCompanyAudit()
        })
        .catch((err: any) => {
          console.error('Silent refresh failed:', err)
          if (!getToken() || err?.code === 401) {
            clearLocalAuthState()
          }
        })
    }

    // 清理 URL 参数（逻辑同前）
    if (route.query?.exchange || route.query?.prjId) {
      const cleanQuery = { ...route.query }
      delete cleanQuery.exchange
      delete cleanQuery.prjId
      return navigateTo({ path: route.path, query: cleanQuery, replace: true })
    }
  }
  // 路径 C: 未登录处理
  else if (route.meta.needToken) {
    go2LoginPage(route.fullPath || route.path)
  } else {
    myUserStore.resetUserCompInfo()
  }
}

/**
 * 辅助方法：setProjectId
 */
const setProjectId = async (route: any) => {
  const prjId = route?.query?.prjId
  const domain = getFirstDomain() || undefined
  const expirationTime = 60 * 30

  if (prjId && prjId.trim() !== '') {
    sessionStorage.setItem('prjId', prjId)
    const target = useCookie('prjId', { maxAge: expirationTime, domain })
    target.value = prjId
    delete route.query.prjId
  } else {
    const existingPrjId = useCookie('prjId')
    if (existingPrjId.value) {
      const cookieSetter = useCookie('prjId', {
        maxAge: expirationTime,
        domain,
      })
      cookieSetter.value = existingPrjId.value
    }
  }
}

/**
 * 辅助方法：setAppConfig
 */
const setAppConfig = async () => {
  if (!appUtils || !appUtils.isApp) return
  try {
    const { changeAppConfig, changeAppFlag } = useMySystemStore()
    changeAppFlag(true)
    const getAppMsg = await appUtils.postMessage('getAppTokenAndLang', {})
    if (getAppMsg) {
      changeAppConfig({
        appTerminalType: getAppMsg.terminalType,
        appToken: getAppMsg.token,
      })
    }
  } catch (err) {
    console.error('App Native Bridge Error:', err)
  }
}
